<template>
    <div class="team-member-edit-communication">
        <grid
            ref="grid"
            :headerHeight="0"
            :isSearch="false"
            :columnDefs="columnDefs"
            :fetchCallback="fetchCallback"
            :onCellClicked="onCellClicked"
        >
            <responsive-button :icon="['fas', 'envelope']" :modal="composeModalID" variant="primary">
                Compose
            </responsive-button>
            <validation-observer
                :initial-values="{ subject: values.subject, body: values.body }"
                ref="form"
                v-slot="{ handleSubmit, errors, meta }"
            >
                <modal
                    :id="composeModalID"
                    title="Send Email"
                    ok-title="Send"
                    :ok-disabled="!meta.valid || composeLoading"
                    @ok.prevent="handleSubmit(_, compose)"
                >
                    <b-form @submit.prevent="handleSubmit(_, compose)">
                        <b-row>
                            <b-col>
                                <validation-provider name="subject" v-slot="{ handleChange }" rules="required">
                                    <form-input
                                        label="Subject:"
                                        v-model="values.subject"
                                        :left-icon="['fas', 'tag']"
                                        :error="errors.subject"
                                        @input="handleChange"
                                    ></form-input>
                                </validation-provider>
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col>
                                <validation-provider name="attachment" v-slot="{ handleChange }">
                                    <form-file
                                        label="Attachment:"
                                        placeholder="Upload and attach a remote file"
                                        v-model="values.attachment"
                                        :left-icon="['fas', 'file']"
                                        :error="errors.attachment"
                                        @input="handleChange"
                                    ></form-file>
                                </validation-provider>
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col>
                                <validation-provider name="body" v-slot="{ handleChange }" rules="required">
                                    <form-textarea
                                        label="Body:"
                                        v-model="values.body"
                                        class="lx-email-body"
                                        :left-icon="['fas', 'note-sticky']"
                                        :error="errors.body"
                                        @input="handleChange"
                                    ></form-textarea>
                                </validation-provider>
                            </b-col>
                        </b-row>
                    </b-form>
                </modal>
            </validation-observer>
            <modal :id="emailBodyModalID" :title="modalTitle" scrollable>
                <busy-modal :show="emailBodyIframeLoading"></busy-modal>
                <iframe
                    :id="emailBodyIframeID"
                    width="100%"
                    height="100%"
                    frameborder="0"
                    scrolling="no"
                    marginheight="0"
                    marginwidth="0"
                    :src="currentEmailBodyUrl"
                    @load="emailBodyIframeLoaded"
                ></iframe>
            </modal>
        </grid>
    </div>
</template>

<script>
    import isEmpty from 'lodash/isEmpty';
    import API from '../../../../api';
    import BusyModal from '../../../generic/busy-modal/busy-modal.vue';
    import FormFile from '../../../generic/form-file/form-file.vue';
    import FormInput from '../../../generic/form-input/form-input.vue';
    import FormTextarea from '../../../generic/form-textarea/form-textarea.vue';
    import Grid from '../../../generic/grid/grid.vue';
    import ResponsiveButton from '../../../generic/responsive-button/responsive-button.vue';
    import Modal from '../../../generic/modal/modal.vue';
    import { formatResponseError } from '../../../../utils/api';
    import { values } from 'lodash';

    export default {
        name: 'TeamMemberEditCommunication',
        components: {
            BusyModal,
            FormFile,
            FormInput,
            FormTextarea,
            Grid,
            ResponsiveButton,
            Modal,
        },
        props: {
            teamMember: Object,
        },
        data() {
            return {
                composeLoading: false,
                composeModalID: 'compose-modal',
                emailBodyModalID: 'email-body-modal',
                emailBodyIframeID: 'email-body-iframe',
                emailBodyIframeLoading: false,
                currentEmailBodyUrl: undefined,
                values: this.getInitialValues(),
                columnDefs: [
                    {
                        headerName: '',
                        field: 'subject',
                        minWidth: 300,
                        cellClass: 'text-primary lx-clickable-cell',
                        valueGetter: ({ data }) =>
                            `${data.subject} ${data.msg_type == 0 ? `by ${data.from_email}` : ''}`,
                    },
                    {
                        field: '',
                        minWidth: 0,
                        headerName: '',
                        autoWidthExclude: true,
                        sortable: false,
                    },
                    {
                        headerName: '',
                        field: 'attachment_url',
                        type: 'attachmentColumn',
                        sortable: false,
                        maxWidth: 61,
                        minWidth: 61,
                    },
                    {
                        type: 'dateTimeColumn',
                        field: 'date',
                        headerName: '',
                        sort: 'desc',
                        minWidth: 200,
                        maxWidth: 215,
                        cellStyle: { textAlign: 'right' },
                        cellRendererParams: {
                            isBadge: true,
                        },
                    },
                ],
                offset: 0,
                limit: 20,
                sort: 'date',
                desc: false,
                search: '',
                msgType: 0,
            };
        },
        computed: {
            fetchCallback() {
                return ({ offset, limit, sort, desc, search }) => {
                    this.offset = offset;
                    this.limit = limit;
                    this.sort = sort;
                    this.desc = desc;
                    this.search = search;
                    return API.get('/app/communication/communication/grid/', {
                        params: {
                            offset,
                            limit,
                            sort,
                            desc,
                            search,
                            user: this.teamMember.id,
                        },
                    });
                };
            },
            modalTitle() {
                return `${this.msgType === 0 ? 'Email' : 'Notification'} Content`;
            },
        },
        methods: {
            getInitialValues() {
                return {
                    subject: 'Message from Lodgix.com',
                    body: '',
                    attachment: null,
                };
            },
            refresh() {
                this.$refs.grid.refreshDataSource();
            },
            onCellClicked({ column, data }) {
                if (column.colId === 'subject') {
                    this.emailBodyIframeLoading = true;
                    this.msgType = data.msg_type;
                    this.values = this.getInitialValues();
                    this.$bvModal.show(this.emailBodyModalID);
                    this.currentEmailBodyUrl = `/app/communication/communication/${data.id}/body/`;
                }
            },
            emailBodyIframeLoaded() {
                this.emailBodyIframeLoading = false;
                const iframe = document.getElementById(this.emailBodyIframeID);
                iframe.style.height = iframe.contentWindow.document.body.offsetHeight + 'px';
            },
            async compose() {
                this.composeLoading = true;

                try {
                    await this.$store.dispatch('teamMember/sendEmail', { id: this.teamMember.id, ...this.values });
                    this.refresh();
                    this.$nextTick(() => {
                        this.$bvModal.hide(this.composeModalID);
                    });
                } catch (err) {
                    const { generic, fields } = formatResponseError(err);

                    if (generic) {
                        this.errorToast({ text: generic });
                    }
                    if (!isEmpty(fields)) {
                        this.$refs.form.setErrors(fields);
                    }
                } finally {
                    this.composeLoading = false;
                }
            },
        },
    };
</script>

<style lang="scss" scoped>
    .team-member-edit-communication {
        height: 100%;
        .lx-grid {
            height: 100% !important;
        }
    }
    :deep(.lx-email-body) {
        textarea {
            min-height: 200px !important;
        }
    }

    :deep(.lx-clickable-cell) {
        text-decoration: underline;
        cursor: pointer;
    }
</style>
